import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from '../../../../shared/i18n';
import { formatSessionTime } from '../../../../shared/utils/formatDateTime';
import { monitoredActivitiesKidApi } from '../../api/monitoredActivities';

const SESSION_STATUSES = ['completed', 'approved', 'rejected', 'cancelled'];

const ICON_SYMBOLS = {
  soccer: '⚽',
  basketball: '🏀',
  parkour: '🤸',
};

const IconPreview = ({ iconType, customIconPath, size = 56 }) => {
  const boxStyle = {
    width: size,
    height: size,
    borderRadius: 16,
    background: 'linear-gradient(135deg, #eef2ff 0%, #ede9fe 100%)',
    flexShrink: 0,
  };
  if (iconType === 'custom' && customIconPath) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={boxStyle}>
        <img
          src={customIconPath}
          alt={iconType}
          style={{ width: size * 0.7, height: size * 0.7, objectFit: 'contain' }}
        />
      </div>
    );
  }
  const symbol = ICON_SYMBOLS[iconType] || '📷';
  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{ ...boxStyle, fontSize: size * 0.55, lineHeight: 1 }}
      aria-label={iconType}
    >
      {symbol}
    </div>
  );
};

const PlayIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 384 512" aria-hidden="true">
    <path d="M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80V432c0 17.4 9.4 33.4 24.5 41.9s33.7 8.1 48.5-.9L361 297c14.3-8.7 23-24.2 23-41s-8.7-32.2-23-41L73 39z"/>
  </svg>
);

const StatusTabIcon = ({ status }) => {
  const props = {
    xmlns: 'http://www.w3.org/2000/svg',
    width: 14,
    height: 14,
    fill: 'currentColor',
    'aria-hidden': true,
    style: { verticalAlign: '-2px' },
  };
  switch (status) {
    case 'completed':
      return (
        <svg {...props} viewBox="0 0 384 512">
          <path d="M0 32C0 14.3 14.3 0 32 0H64 320h32c17.7 0 32 14.3 32 32s-14.3 32-32 32V75c0 42.4-16.9 83.1-46.9 113.1L237.3 256l67.9 67.9c30 30 46.9 70.7 46.9 113.1v11c17.7 0 32 14.3 32 32s-14.3 32-32 32H320 64 32c-17.7 0-32-14.3-32-32s14.3-32 32-32V437c0-42.4 16.9-83.1 46.9-113.1L146.7 256 78.9 188.1C48.9 158.1 32 117.4 32 75V64C14.3 64 0 49.7 0 32zM96 64V75c0 25.5 10.1 49.9 28.1 67.9L192 210.7l67.9-67.9c18-18 28.1-42.4 28.1-67.9V64H96zm0 384H288V437c0-25.5-10.1-49.9-28.1-67.9L192 301.3l-67.9 67.9C106.1 387.2 96 411.6 96 437v11z"/>
        </svg>
      );
    case 'approved':
      return (
        <svg {...props} viewBox="0 0 448 512">
          <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
        </svg>
      );
    case 'rejected':
      return (
        <svg {...props} viewBox="0 0 384 512">
          <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/>
        </svg>
      );
    case 'cancelled':
      return (
        <svg {...props} viewBox="0 0 512 512">
          <path d="M367.2 412.5C335.9 434.9 297.5 448 256 448C150 448 64 362 64 256c0-41.5 13.1-79.9 35.5-111.2L367.2 412.5zM412.5 367.2L144.8 99.5C176.1 77.1 214.5 64 256 64c106 0 192 86 192 192c0 41.5-13.1 79.9-35.5 111.2zM0 256a256 256 0 1 0 512 0A256 256 0 1 0 0 256z"/>
        </svg>
      );
    default:
      return null;
  }
};

const ClockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 512 512" aria-hidden="true" style={{ verticalAlign: '-2px' }}>
    <path d="M256 0a256 256 0 1 1 0 512A256 256 0 1 1 256 0zM232 120V256c0 8 4 15.5 10.7 20l96 64c11 7.4 25.9 4.4 33.3-6.7s4.4-25.9-6.7-33.3L280 243.2V120c0-13.3-10.7-24-24-24s-24 10.7-24 24z"/>
  </svg>
);

const StatusBadge = ({ status, t }) => {
  const styles = {
    approved: { bg: '#dcfce7', color: '#15803d' },
    rejected: { bg: '#fee2e2', color: '#b91c1c' },
    cancelled: { bg: '#e5e7eb', color: '#4b5563' },
    completed: { bg: '#fef3c7', color: '#a16207' },
  };
  const s = styles[status] || styles.completed;
  return (
    <span
      className="fw-semibold rounded-pill px-3 py-1 text-nowrap"
      style={{ background: s.bg, color: s.color, fontSize: 13 }}
    >
      {t(`children.monitored_sessions.status.${status}`)}
    </span>
  );
};

const ActivitiesTab = ({ t }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    monitoredActivitiesKidApi
      .list()
      .then((res) => setActivities(res.data.monitored_activities))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleStart = async (activityId) => {
    setStarting(activityId);
    try {
      const res = await monitoredActivitiesKidApi.createSession(activityId);
      const session = res.data.monitored_activity_session;
      navigate(`/monitored-sessions/${session.id}`);
    } catch (e) {
      setError(e.message);
      setStarting(null);
    }
  };

  if (loading) return <div className="text-center py-4"><div className="spinner-border" role="status" /></div>;
  if (error) return <div className="alert alert-danger rounded-4">{error}</div>;
  if (activities.length === 0) {
    return <div className="alert alert-info rounded-4">{t('children.monitored_activities.empty')}</div>;
  }

  return (
    <div className="d-flex flex-column gap-3">
      {activities.map((a) => (
        <div
          key={a.id}
          className="card border-0 rounded-4 shadow-sm"
          data-testid={`kid-monitored-activity-${a.id}`}
        >
          <div className="card-body p-3">
            <div className="d-flex align-items-center gap-3 mb-3">
              <IconPreview iconType={a.icon_type} customIconPath={a.custom_icon_path} />
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div
                  className="fw-bold mb-1"
                  style={{
                    fontSize: '1.05rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {a.name}
                </div>
                <div className="text-secondary small d-inline-flex align-items-center gap-1">
                  <ClockIcon />
                  {t('children.monitored_activities.length_display', { minutes: a.length_minutes })}
                </div>
              </div>
            </div>
            <button
              type="button"
              className="btn w-100 fw-semibold rounded-4 d-inline-flex align-items-center justify-content-center gap-2 text-white"
              disabled={starting === a.id}
              onClick={() => handleStart(a.id)}
              data-testid={`kid-monitored-start-${a.id}`}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                minHeight: 52,
                fontSize: 16,
                opacity: starting === a.id ? 0.7 : 1,
              }}
            >
              {starting === a.id ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                <PlayIcon />
              )}
              {t('children.monitored_activities.start')}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const SessionsTab = ({ t }) => {
  const [status, setStatus] = useState('completed');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    monitoredActivitiesKidApi
      .listSessions(status)
      .then((res) => setSessions(res.data.monitored_activity_sessions))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [status]);

  return (
    <div>
      <div
        className="mb-3"
        data-testid="kid-session-status-tabs"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 8,
        }}
      >
        {SESSION_STATUSES.map((s) => {
          const active = status === s;
          return (
            <button
              key={s}
              type="button"
              className="btn rounded-pill d-flex align-items-center justify-content-center gap-2"
              onClick={() => setStatus(s)}
              data-testid={`kid-session-tab-${s}`}
              style={{
                background: active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f3f4f6',
                color: active ? '#fff' : '#374151',
                border: 'none',
                fontWeight: 600,
                fontSize: 14,
                padding: '10px 12px',
                minWidth: 0,
              }}
            >
              <StatusTabIcon status={s} />
              <span className="text-truncate">{t(`children.monitored_sessions.status.${s}`)}</span>
            </button>
          );
        })}
      </div>

      {loading && <div className="text-center py-4"><div className="spinner-border" role="status" /></div>}
      {error && <div className="alert alert-danger rounded-4">{error}</div>}
      {!loading && sessions.length === 0 && (
        <div className="alert alert-info rounded-4">{t('children.monitored_sessions.empty')}</div>
      )}

      <div className="d-flex flex-column gap-2">
        {sessions.map((s) => (
          <div
            key={s.id}
            className="card border-0 rounded-4 shadow-sm"
            data-testid={`kid-monitored-session-${s.id}`}
          >
            <div className="card-body p-3 d-flex align-items-center gap-3">
              <IconPreview
                iconType={s.monitored_activity?.icon_type}
                customIconPath={s.monitored_activity?.custom_icon_path}
                size={48}
              />
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div
                  className="fw-bold"
                  style={{
                    fontSize: '1rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.monitored_activity?.name}
                </div>
                <div className="text-secondary small">
                  {formatSessionTime(s.started_at)}
                </div>
              </div>
              <StatusBadge status={s.status} t={t} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const MonitoredActivityList = () => {
  const { t } = useTranslation();

  return (
    <div
      className="container-fluid px-3 py-3"
      style={{ maxWidth: 720 }}
      data-testid="monitored-activities-list"
    >
      <h2 className="fw-bold mb-3" style={{ fontSize: 'clamp(1.35rem, 5vw, 1.75rem)' }}>
        {t('children.monitored_activities.title')}
      </h2>

      <section className="mb-4" data-testid="kid-monitored-activities-section">
        <h3 className="h6 fw-bold text-uppercase text-secondary mb-3" style={{ letterSpacing: '0.05em' }}>
          {t('children.monitored_activities.section_start')}
        </h3>
        <ActivitiesTab t={t} />
      </section>

      <section data-testid="kid-monitored-sessions-section">
        <h3 className="h6 fw-bold text-uppercase text-secondary mb-3" style={{ letterSpacing: '0.05em' }}>
          {t('children.monitored_sessions.title')}
        </h3>
        <SessionsTab t={t} />
      </section>
    </div>
  );
};

export default MonitoredActivityList;
